import { useAuthStoreV1 } from '@/stores/authStore_v1';

/**
 * Capability and enterprise state for the CURRENTLY SELECTED connection.
 *
 * NO QUERY OF ITS OWN, deliberately. Everything here already arrived in the single
 * `["user-data", token]` GetUserData response and was hydrated into `authStore_v1` by `setUser`.
 * Adding a hook that fetched any of it again would be a second source of the same truth and a
 * request the app already made.
 *
 * PER CONNECTION, not per user. `GetUserData` returns an array of organizations, each with its own
 * `myRole.capabilityKeys` and its own `isEnterprise`. Reading either from the user level would be
 * wrong in the ordinary case of one person holding a production connection and a sandbox with
 * different roles — so every selector here reads `selectedOrg`, and switching connections re-renders
 * consumers with the new answer. There is nowhere for a previous connection's capabilities to be
 * cached, because nothing is cached: these are selectors over the live store.
 */

/**
 * The selected connection's capability keys.
 *
 * A stable empty array would be nicer for referential equality, but zustand's selector already
 * bails out when the returned value is identical — and `capabilityKeys` is the same array instance
 * from the store between renders, so only the `?? []` fallback allocates. Consumers pass it to pure
 * predicates rather than into dependency arrays, so that allocation costs nothing.
 */
export function useCapabilityKeys(): string[] {
  return useAuthStoreV1((s) => s.selectedOrg?.myRole?.capabilityKeys ?? EMPTY);
}

/** One shared empty array, so an unassigned role does not produce a new identity per render. */
const EMPTY: string[] = [];

/** Whether the selected connection grants a specific module capability. */
export function useHasCapability(moduleKey: string | null): boolean {
  return useAuthStoreV1((s) => {
    if (!moduleKey) return true;
    return (s.selectedOrg?.myRole?.capabilityKeys ?? EMPTY).includes(moduleKey);
  });
}

/**
 * Whether the selected connection is billed as an enterprise contract.
 *
 * Defaults to `false` when the field is absent, so a backend predating it behaves exactly as before
 * rather than having `undefined` read as some third state.
 */
export function useIsEnterpriseConnection(): boolean {
  return useAuthStoreV1((s) => s.selectedOrg?.isEnterprise === true);
}

/**
 * Whether authorization can be decided yet.
 *
 * The race this closes: `GetUserData` is in flight, `capabilityKeys` is empty because nothing has
 * arrived, and an empty list denies everything. Rendering an authorization decision from that would
 * flash "not available" on a module the user owns — or, guarding the other way round, would show
 * every module for a moment and then remove them.
 *
 * Mirrors the sidebar's existing `isNavLoading` condition rather than inventing a second notion of
 * "loading": unresolved means the user fetch is running AND no organizations have landed. Once any
 * organization exists the answer is real, even mid-refetch.
 */
export function useIsAuthorizationResolved(): boolean {
  return useAuthStoreV1((s) => !(s.isLoadingUser && s.organizations.length === 0));
}
